import { settings } from '@/main/config/settings'
import { createContext, ReactNode, useCallback, useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { setRecaptchaToken } from './recaptcha-token-store'
import { FeatureFlagName, useFeatureFlags } from '@/infra/feature-flag'

export interface RecaptchaContextType {
  executeRecaptcha: (callback: () => Promise<unknown>) => Promise<void>
}

export const RecaptchaContext = createContext<RecaptchaContextType | undefined>(undefined)

interface RecaptchaProviderProps {
  children: ReactNode
}

export const RecaptchaProvider: React.FC<RecaptchaProviderProps> = ({ children }) => {
  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const [onSuccessCallback, setOnSuccessCallback] = useState<{
    callback: () => Promise<unknown>
    resolve: () => void
    reject: (error: unknown) => void
  } | null>(null)

  const { recaptchaEnabled } = useFeatureFlags({
    recaptchaEnabled: FeatureFlagName.INVISIBLE_RECAPTCHA
  })

  const executeRecaptcha = useCallback(
    async (callback: () => Promise<unknown>) => {
      if (!recaptchaEnabled) {
        await callback()
        return
      }

      return new Promise<void>((resolve, reject) => {
        if (!recaptchaRef.current) {
          console.error('reCAPTCHA not ready')
          reject(new Error('reCAPTCHA not ready'))
          return
        }

        setOnSuccessCallback({
          callback,
          resolve,
          reject
        })

        recaptchaRef.current.executeAsync()
      })
    },
    [recaptchaEnabled]
  )

  const onRecaptchaChange = async (token: string | null) => {
    setRecaptchaToken(token)
    if (token && onSuccessCallback) {
      try {
        await onSuccessCallback.callback()
        onSuccessCallback.resolve()
      } catch (error) {
        console.error('Callback execution failed after getting token', error)
        onSuccessCallback.reject(error)
      } finally {
        recaptchaRef.current?.reset()
        setOnSuccessCallback(null)
      }
    }
  }

  return (
    <RecaptchaContext.Provider value={{ executeRecaptcha }}>
      {children}
      {recaptchaEnabled && (
        <ReCAPTCHA
          ref={recaptchaRef}
          size="invisible"
          sitekey={settings.RECAPTCHA_SITE_KEY}
          onChange={onRecaptchaChange}
        />
      )}
    </RecaptchaContext.Provider>
  )
}
